import { FormFieldEnums } from "shared/types/enums";
import { SelectInput, TextInput } from "shared/components/forms/inputs";
import { useState } from "react";
import { useUserDetailsStore } from "shared/stores/userDetailsStore";
import "styles/flows/forex/passwordManager/passwordSetupStep.css";
import { useTranslation } from "react-i18next";
import { AccountsSelect } from "shared/components/forms/inputs/AccountsSelect";
import { useDeviceStore } from "shared/stores/deviceStore";
import { colorByAccountType, createAccountName } from "shared/utils";
import { PasswordManagerFormData } from "../types";
import { useSharedStore } from "shared/stores/sharedStore";
import { useSearchParams } from "react-router-dom";

const PasswordSetupStep = ({
  formValid,
  setFormValid,
  formData,
  handleInputChange,
  showTradingAccounts,
}: {
  formValid: boolean;
  setFormValid: React.Dispatch<React.SetStateAction<boolean>>;
  formData: PasswordManagerFormData;
  handleInputChange: (id: string, value: unknown) => void;
  showTradingAccounts: boolean;
}) => {
  const { t } = useTranslation();
  const isMobile = useDeviceStore((state) => state.isMobile);
  const [searchParams] = useSearchParams();
  const isFrontEgg =
    searchParams.get("frontegg")?.toLowerCase() === "true" &&
    !showTradingAccounts;

  const [accountSelected, setAccountSelected] = useState(formData.account);
  const { brand } = useSharedStore();

  const onOptionSelect = (valueObject: { name: string; value: string }) => {
    setAccountSelected(valueObject);
    handleInputChange("account", valueObject);
  };

  let titleLocalized = "PasswordManager.Setup.Title";
  let subTitleLocalized = "PasswordManager.Setup.SubTitle";
  let subTitleNoteLocalized = "PasswordManager.Setup.SubTitleNote";

  switch (brand) {
    case "thor-thunder":
      // Using default values, no need to reassign
      break;
    case "draco-might":
      titleLocalized = "PasswordManager.Setup.TitleGCM";
      subTitleNoteLocalized = "PasswordManager.Setup.SubTitleNoteGCM";
      break;
    case "zephir-blade":
      titleLocalized = "PasswordManager.Setup.TitleKP";
      subTitleNoteLocalized = "PasswordManager.Setup.SubTitleNoteKP";
      break;
    case "mermaid-haven":
      titleLocalized = "PasswordManager.Setup.TitleGCM";
      break;
    default:
      console.error("Brand not found");
      break;
  }

  return (
    <div className="password-setup">
      <main className="password-setup-container">
        <h2
          style={{
            marginBottom: showTradingAccounts ? "0rem" : "24px",
          }}
        >
          {t(titleLocalized)}
        </h2>
        {showTradingAccounts && (
          <h3>
            {t(subTitleLocalized)}
            <br />
            {t(subTitleNoteLocalized)}
          </h3>
        )}

        {showTradingAccounts ? (
          <div
            style={{
              marginTop: "12px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {!isMobile ? (
              <AccountsSelect
                formData={formData}
                onOptionSelect={onOptionSelect}
              />
            ) : (
              <SelectInput
                type={FormFieldEnums.Select}
                required
                className="select-accounts"
                placeholder={t("PasswordManager.Setup.SelectAccount")}
                id="select-accounts"
                options={<AccountsOptions />}
                value={
                  (accountSelected as { name: string; value: string }).name
                }
                onSelected={(account) => {
                  console.log(account);
                  setAccountSelected(account);
                  handleInputChange("account", account);
                }}
                withSearch={false}
                detent="content-height"
                title={t("PasswordManager.Setup.SelectAccount")}
              />
            )}
            <div style={{ marginTop: "24px" }}>
              {accountSelected && (
                <PasswordsChange
                  formValid={formValid}
                  setFormValid={setFormValid}
                  formData={formData}
                  handleInputChange={handleInputChange}
                  isFrontEgg={isFrontEgg}
                />
              )}
            </div>
          </div>
        ) : (
          <PasswordsChange
            formValid={formValid}
            setFormValid={setFormValid}
            formData={formData}
            handleInputChange={handleInputChange}
            isFrontEgg={isFrontEgg}
          />
        )}
      </main>
      <div className="next-submit-button">
        <button disabled={!formValid}>
          {t("PasswordManager.Setup.ChangePasswordBtn")}
        </button>
      </div>
    </div>
  );
};

const AccountsOptions = ({
  onOptionSelect,
}: {
  onOptionSelect?: (option: { name: string; value: string }) => void;
}) => {
  const userDetails = useUserDetailsStore((state) => state.userDetails);

  return (
    <div className="accounts-options">
      {userDetails?.Accounts.map((account, index) => {
        const accountName = createAccountName(account);

        return (
          <div
            key={`${account.Name}/${index}`}
            className="account"
            onClick={() =>
              onOptionSelect?.({
                name: accountName,
                value: account.Name,
              })
            }
          >
            <div
              className="ellipse"
              style={{
                backgroundColor: colorByAccountType(
                  account.License,
                  account.IsDemo
                ),
              }}
            ></div>
            <div className="account-details">
              <div>{accountName}</div>
              <div>TP: {account.Name}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const PasswordsChange = ({
  formValid,
  setFormValid,
  formData,
  handleInputChange,
  isFrontEgg,
}: {
  formValid: boolean;
  setFormValid: React.Dispatch<React.SetStateAction<boolean>>;
  formData: PasswordManagerFormData;
  handleInputChange: (id: string, value: unknown) => void;
  isFrontEgg: boolean;
}) => {
  const { t } = useTranslation();

  const [strengthItems, setStrengthItems] = useState(formData.strengthItems);
  const [showHelper, setShowHelper] = useState(formData.password);

  // Check password validation
  const validatePassword = (value: string) => {
    const newStrengthItems = strengthItems.map((item) => ({
      ...item,
      isValid: item.regex.test(value),
    }));
    handleInputChange("strengthItems", newStrengthItems);
    setStrengthItems(newStrengthItems);
    return newStrengthItems.every((item) => item.isValid);
  };

  const handleFieldChange =
    (field: keyof PasswordManagerFormData) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      handleInputChange(field, value);

      // Simulate updated values
      const password = field === "password" ? value : formData.password;
      const reenteredPassword =
        field === "reenteredPassword" ? value : formData.reenteredPassword;
      const currentPassword =
        field === "currentPassword" ? value : formData.currentPassword;

      let isPasswordValid = strengthItems.every((item) => item.isValid);
      const doPasswordsMatch = password === reenteredPassword;
      const isCurrentPassword =
        currentPassword !== undefined && currentPassword !== "";

      if (field === "password") {
        isPasswordValid = validatePassword(value);
        setShowHelper(value);
      }

      if (isFrontEgg) {
        setFormValid(isPasswordValid && doPasswordsMatch && isCurrentPassword);
      } else {
        setFormValid(isPasswordValid && doPasswordsMatch);
      }
    };

  return (
    <div className="passwords-container">
      <input
        style={{ display: "none" }}
        type="text"
        name="username"
        autoComplete="username"
        aria-hidden="true"
      />
      {isFrontEgg && (
        <TextInput
          onChange={handleFieldChange("currentPassword")}
          placeholder={t("PasswordManager.Setup.CurrentPassword")}
          type={FormFieldEnums.Password}
          required
          id="current-password"
          autoComplete="current-password"
          showPasswordToggle={true}
          value={formData.currentPassword}
          styleProps={{ marginBottom: "2.4rem" }}
        />
      )}
      <TextInput
        onChange={handleFieldChange("password")}
        placeholder={t("PasswordManager.Setup.Password")}
        type={FormFieldEnums.Password}
        required
        fixCordova={true}
        id="new-password"
        name="newUserPass"
        showPasswordToggle={true}
        value={formData.password}
      />

      <div
        className={`password-validation-helper ${showHelper ? "show" : ""}`}
        tabIndex={-1}
      >
        <h3>{t("PasswordManager.Setup.PasswordHelperTitle")}</h3>
        <div className="validation-helper">
          {strengthItems.map((item) => (
            <div key={item.name} className="validation-item">
              <div className={`circle ${item.isValid && "valid"}`}></div>
              <span>{t(item.name)}</span>
            </div>
          ))}
        </div>
      </div>

      <TextInput
        onChange={handleFieldChange("reenteredPassword")}
        placeholder={t("PasswordManager.Setup.ReEnterPassword")}
        type={FormFieldEnums.Password}
        required
        fixCordova={true}
        id="reenter-password"
        value={formData.reenteredPassword}
        styleProps={{
          border:
            formData.password &&
            formData.reenteredPassword &&
            formData.reenteredPassword !== formData.password
              ? "1px solid #DF1C7A"
              : "",

          marginTop: "2.4rem",
        }}
        showPasswordToggle={true}
      />
      {!formValid &&
        formData.reenteredPassword &&
        formData.password &&
        formData.password !== formData.reenteredPassword && (
          <p className="error-message">
            {t("PasswordManager.Setup.PasswordMatchError")}
          </p>
        )}
    </div>
  );
};

export default PasswordSetupStep;
